import { LandingPage } from '../../components'

const PrivacyPolicy = () => {
  return (
    <LandingPage
      title='Privacy Policy'
      description='Privacy policy for wowaudit.'
    >
      <div className='space-y-6'>
        <p className='text-sm text-muted-foreground'>
          Last updated: June 17, 2026
        </p>

        <section className='space-y-2 mt-8'>
          <h2>Introduction</h2>
          <p>
            wowaudit is a World of Warcraft guild management tool. This privacy
            policy explains what personal data we collect, how we use it, and
            your rights regarding that data when you use wowaudit.com and its
            subdomains.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Information we collect</h2>

          <h4>Account data</h4>
          <p>
            Accounts are created through Battle.net OAuth. When you log in we
            receive and store your Blizzard account ID, BattleTag, and a
            short-lived OAuth access token used to fetch your World of Warcraft
            characters.
          </p>

          <h4 className='mt-6'>Optional linked accounts</h4>
          <p>
            You can optionally link additional accounts for specific features.
            We store only what is needed to power those features:
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Google</strong>: email address, used as a fallback login
              method and for email notifications.
            </li>
            <li>
              <strong>Discord</strong>: Discord user ID, username, and avatar
              URL, used to send notifications and @mentions.
            </li>
            <li>
              <strong>Patreon</strong>: email address and membership
              status/tier, used to verify premium access.
            </li>
            <li>
              <strong>Warcraft Logs</strong>: WCL username and token, used to
              retrieve log data for your team.
            </li>
          </ul>

          <h4 className='mt-6'>World of Warcraft character data</h4>
          <p>
            We fetch your World of Warcraft characters from the Blizzard API
            using your OAuth token. This includes character names, realm, class,
            race, item level, achievements, and guild memberships. This data is
            regularly refreshed to keep audit information up to date.
          </p>

          <h4 className='mt-6'>Guild application data</h4>
          <p>
            If you apply to join a guild through wowaudit, you may submit
            additional information such as your age, country, role preference,
            free-text answers to application questions, and file attachments.
            This information is visible to the guild you apply to.
          </p>

          <h4 className='mt-6'>Settings and preferences</h4>
          <p>
            We store your account preferences, including BattleTag visibility
            settings (whether other members can see your full BattleTag),
            alt-character visibility, and notification preferences.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>How we use your information</h2>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Authentication</strong>: to identify you when you log in
              and maintain your session.
            </li>
            <li>
              <strong>Character data</strong>: to compute gear summaries,
              in-game activity stats and other audit data for your team.
            </li>
            <li>
              <strong>Notifications</strong>: to send reminders and other
              messages to your email address or Discord account (unless you opt
              out).
            </li>
            <li>
              <strong>Premium verification</strong>: to check your Patreon
              membership status and grant premium features accordingly.
            </li>
            <li>
              <strong>Recruitment</strong>: to let guilds you apply to reach out
              to you.
            </li>
          </ul>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Cookies and local storage</h2>
          <p>
            We only use cookies that are essential for login and security. We do
            not use <strong>any</strong> tracking, analytics, or advertising
            cookies.
          </p>
          <p>
            In addition to cookies, we store UI preferences (such as selected
            filters, theme, and draft form data) in your browser&apos;s local
            storage. This data never leaves your device and is not sent to our
            servers.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Third-party services</h2>
          <p>
            We rely on the following third-party services to operate wowaudit.
            Each service has its own privacy policy governing how they handle
            data:
          </p>
          <ul className='list-disc pl-5 space-y-1 text-sm'>
            <li>
              <strong>Blizzard / Battle.net</strong>: authentication and World
              of Warcraft character data.
            </li>
            <li>
              <strong>Google</strong>: optional account linking for login and
              email notifications.
            </li>
            <li>
              <strong>Discord</strong>: optional account linking for
              notifications and bot commands.
            </li>
            <li>
              <strong>Patreon</strong>: optional account linking for premium
              membership verification.
            </li>
            <li>
              <strong>Warcraft Logs</strong>: optional account linking to
              retrieve log data.
            </li>
            <li>
              <strong>Raider.IO</strong>: Mythic+ score and character enrichment
              data fetched by our backend.
            </li>
            <li>
              <strong>SendGrid</strong>: transactional email delivery
              (notifications).
            </li>
            <li>
              <strong>Sentry</strong>: server-side error monitoring to help us
              identify and fix bugs.
            </li>
            <li>
              <strong>Wowhead</strong>: item tooltip scripts loaded on most
              pages to display in-game item information.
            </li>
          </ul>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data visibility and sharing</h2>
          <p>
            Your BattleTag visibility can be controlled in your account settings
            : you can choose to show your full BattleTag, hide the discriminator
            numbers, display a custom nickname, or hide it entirely.
          </p>
          <p>
            Teams that track your characters can see your character data, such
            as item level, gear, and in-game activity stats. You can control
            which teams can add your characters to their roster in your account
            settings.
          </p>
          <p>
            We do not sell, rent, or trade your personal data to any third
            parties for any purpose.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data retention</h2>
          <p>
            We retain your account data for as long as your account is active.
            OAuth access tokens are refreshed periodically and are not stored
            beyond their functional lifetime. Character data is refreshed from
            Blizzard&apos;s API and historical records may be kept to power
            audit history features.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Your rights</h2>
          <p>
            You have the right to access, correct, or request deletion of your
            personal data. To exercise any of these rights, contact us at{' '}
            <a
              href='mailto:privacy@wowaudit.com'
              className='underline hover:opacity-80'
            >
              privacy@wowaudit.com
            </a>
            . We will respond within 30 days.
          </p>
          <p>
            You can unlink third-party accounts (Google, Discord, Patreon,
            Warcraft Logs) at any time from your account settings, which removes
            the associated data from our systems.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Security</h2>
          <p>
            We use industry-standard practices to protect your data, including
            encrypted connections (HTTPS), session tokens, and CSRF protection.
            OAuth access tokens are stored server-side and are never exposed to
            the browser.
          </p>
        </section>

        <section className='space-y-2 mt-8'>
          <h2>Data controller</h2>
          <p>
            This site is operated by wowaudit, a sole proprietorship registered
            with the Dutch Chamber of Commerce under number 73736481. For any
            privacy-related questions you can contact us at{' '}
            <a
              href='mailto:privacy@wowaudit.com'
              className='underline hover:opacity-80'
            >
              privacy@wowaudit.com
            </a>
            .
          </p>
        </section>
      </div>
    </LandingPage>
  )
}

export default PrivacyPolicy
